<template>
  <div :class="['tmap', { 'tmap--visible': visible, 'tmap--collapsed': collapsed, 'tmap--expanded': expanded }]">
    <header class="tmap__head">
      <button
        class="tmap__toggle-area"
        type="button"
        @click="collapsed = !collapsed"
        :aria-expanded="!collapsed"
        :aria-label="collapsed ? 'Expand map' : 'Collapse map'"
      >
        <div class="tmap__head-txt">
          <span class="tmap__year">{{ year || '—' }}</span>
          <span class="tmap__sep">·</span>
          <span class="tmap__loc">{{ placeName || 'somewhere' }}</span>
        </div>
      </button>
      <button
        v-if="!collapsed"
        class="tmap__expand"
        type="button"
        @click="toggleExpand"
        :aria-label="expanded ? 'Shrink map' : 'Open full map'"
        :title="expanded ? 'Shrink' : 'Open full map'"
      >{{ expanded ? '⤡' : '⤢' }}</button>
      <button
        v-if="!expanded"
        class="tmap__collapse"
        type="button"
        @click="collapsed = !collapsed"
        :aria-label="collapsed ? 'Expand' : 'Collapse'"
      >{{ collapsed ? '▴' : '▾' }}</button>
    </header>

    <div v-if="expanded" class="tmap__filters">
      <button
        v-for="f in filters"
        :key="f.key"
        type="button"
        :class="['tmap__filter', { 'is-on': activeFilter === f.key }]"
        @click="activeFilter = f.key"
      >
        {{ f.label }}
        <span class="tmap__filter-n">{{ countFor(f.key) }}</span>
      </button>
    </div>

    <div ref="mapHolder" class="tmap__canvas" />

    <footer v-if="expanded" class="tmap__foot">
      <div class="tmap__stat">
        <span class="num">{{ visitedCoords?.length || 0 }}</span> places visited so far ·
        <span class="num">{{ allEvents?.length || 0 }}</span> total events ·
        click a dot to jump
      </div>
      <button class="tmap__close-x" type="button" @click="expanded = false">Close ✕</button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface MapEvent {
  year: number;
  endYear?: number;
  yearText?: string;
  title: string;
  lane: 'life' | 'works' | 'exhibitions';
  kind?: 'context';
  coords: [number, number];
  placeName?: string;
}

const props = defineProps<{
  coords?: [number, number] | null;
  placeName?: string | null;
  year?: string | number | null;
  visible?: boolean;
  visitedCoords?: Array<[number, number]>;
  totalCount?: number;
  allEvents?: MapEvent[];
}>();

const emit = defineEmits<{ (e: 'jumpToYear', year: number): void }>();

const mapHolder = ref<HTMLElement | null>(null);
const collapsed = ref(false);
const expanded = ref(false);
const activeFilter = ref<'all' | 'life' | 'works' | 'exhibitions' | 'context'>('all');

let map: L.Map | null = null;
let activeMarker: L.CircleMarker | null = null;
let visitedLayer: L.LayerGroup | null = null;
let allLayer: L.LayerGroup | null = null;
// Persistent registry of visited-dot markers, keyed by bucketed lat,lng.
// Used so renderVisited can DIFF (add/remove) instead of clear+recreate —
// the clear-and-rebuild made the dots flash to new positions during the
// active-entry flyTo animation.
const visitedMarkers = new Map<string, L.CircleMarker>();

const HOME: L.LatLngTuple = [44.0835, 10.1156];

const filters = [
  { key: 'all', label: 'All' },
  { key: 'life', label: 'Life' },
  { key: 'works', label: 'Works' },
  { key: 'exhibitions', label: 'Exh.' },
  { key: 'context', label: 'Context' },
] as const;

function countFor(key: typeof activeFilter.value): number {
  if (!props.allEvents) return 0;
  if (key === 'all') return props.allEvents.length;
  if (key === 'context') return props.allEvents.filter((e) => e.kind === 'context').length;
  return props.allEvents.filter((e) => !e.kind && e.lane === key).length;
}

const filteredEvents = computed<MapEvent[]>(() => {
  const all = props.allEvents || [];
  if (activeFilter.value === 'all') return all;
  if (activeFilter.value === 'context') return all.filter((e) => e.kind === 'context');
  return all.filter((e) => !e.kind && e.lane === activeFilter.value);
});

function toggleExpand() {
  expanded.value = !expanded.value;
}

onMounted(() => {
  if (!mapHolder.value) return;
  map = L.map(mapHolder.value, {
    center: [46, 10],
    zoom: 4,
    minZoom: 2,
    maxZoom: 9,
    zoomControl: false,
    scrollWheelZoom: false,
    dragging: false,
    doubleClickZoom: false,
    keyboard: false,
    attributionControl: false,
  });

  L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png', {
    subdomains: 'abcd',
    maxZoom: 19,
  }).addTo(map);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png', {
    subdomains: 'abcd',
    maxZoom: 19,
  }).addTo(map);

  visitedLayer = L.layerGroup().addTo(map);
  allLayer = L.layerGroup(); // not yet added; toggled by expanded state

  // Anchor: Miseglia
  L.circleMarker(HOME, {
    radius: 6,
    color: '#C25A3A',
    weight: 2,
    fillColor: '#C25A3A',
    fillOpacity: 0.9,
  }).addTo(map).bindTooltip('Miseglia · home', { direction: 'top', className: 'place-tip' });

  if (props.coords) updateActive(props.coords);
  if (props.visitedCoords) renderVisited(props.visitedCoords);

  // Defensive: re-measure the canvas after the flex layout has fully settled,
  // and again after the becomes-visible CSS transition completes.
  nextTick(() => { map?.invalidateSize(); });
  setTimeout(() => { map?.invalidateSize(); }, 500);
});

// When the widget becomes visible (timeline section scrolls into view),
// the parent's opacity changes from 0 to 1; force a re-measure.
watch(() => props.visible, (v) => {
  if (v) setTimeout(() => { map?.invalidateSize(); }, 50);
});

watch(() => props.coords, (c) => { if (c) updateActive(c); });
watch(() => props.visitedCoords, (v) => { renderVisited(v || []); }, { deep: true });

// When expand toggles: enable interactions, switch layers, repaint
watch(expanded, async (isExp) => {
  await new Promise((r) => setTimeout(r, 320));
  await nextTick();
  if (!map) return;
  map.invalidateSize();
  if (isExp) {
    map.dragging.enable();
    map.scrollWheelZoom.enable();
    map.doubleClickZoom.enable();
    map.keyboard.enable();
    if (visitedLayer) map.removeLayer(visitedLayer);
    if (allLayer) allLayer.addTo(map);
    renderAll();
    // Fit the view to the European cluster of his life
    // (drop the Cape Town and Japan outliers — they stretch the bounds across
    // entire continents and leave nothing readable in the middle).
    const inFrame = (props.allEvents || []).filter(
      (e) => e.coords[0] > 25 && e.coords[0] < 60 && e.coords[1] > -15 && e.coords[1] < 25,
    );
    if (inFrame.length > 0) {
      const bounds = L.latLngBounds(inFrame.map((e) => e.coords));
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 7, animate: true, duration: 0.4 });
    }
  } else {
    map.dragging.disable();
    map.scrollWheelZoom.disable();
    map.doubleClickZoom.disable();
    map.keyboard.disable();
    if (allLayer) map.removeLayer(allLayer);
    if (visitedLayer) visitedLayer.addTo(map);
    if (props.coords) updateActive(props.coords);
  }
});

watch(collapsed, async () => {
  await new Promise((r) => setTimeout(r, 320));
  map?.invalidateSize();
  if (props.coords) updateActive(props.coords);
});

watch([filteredEvents, expanded], () => {
  if (expanded.value) renderAll();
});

function renderVisited(coords: Array<[number, number]>) {
  if (!visitedLayer) return;
  const wanted = new Set<string>();
  for (const c of coords) {
    const key = `${Math.round(c[0] * 10) / 10},${Math.round(c[1] * 10) / 10}`;
    if (wanted.has(key)) continue;
    wanted.add(key);
    if (visitedMarkers.has(key)) continue; // already on map
    const m = L.circleMarker(c, {
      radius: 4,
      color: '#1B1B1F',
      weight: 1,
      fillColor: '#A6A6AA',
      fillOpacity: 0.55,
      opacity: 0.5,
    });
    m.addTo(visitedLayer);
    visitedMarkers.set(key, m);
  }
  // Drop markers that fell out of the visited set (shouldn't happen with the
  // current monotonic scroll-tracking logic but supports filter changes).
  for (const [key, m] of visitedMarkers) {
    if (!wanted.has(key)) {
      visitedLayer.removeLayer(m);
      visitedMarkers.delete(key);
    }
  }
}

function renderAll() {
  if (!allLayer) return;
  allLayer.clearLayers();
  const visitedSet = new Set(
    (props.visitedCoords || []).map((c) => `${Math.round(c[0] * 10) / 10},${Math.round(c[1] * 10) / 10}`),
  );
  for (const ev of filteredEvents.value) {
    const isVisited = visitedSet.has(`${Math.round(ev.coords[0] * 10) / 10},${Math.round(ev.coords[1] * 10) / 10}`);
    const color = ev.kind === 'context'
      ? '#5D5D62'
      : ev.lane === 'life' ? '#1B1B1F'
      : ev.lane === 'works' ? '#7C2F19'
      : '#3A5A6E';

    const m = L.circleMarker(ev.coords, {
      radius: isVisited ? 7 : 5,
      color: color,
      weight: 1.5,
      fillColor: color,
      fillOpacity: isVisited ? 0.85 : 0.35,
    });
    m.bindTooltip(
      `<strong>${ev.yearText || ev.year}</strong> · ${ev.title}<br /><span style="color:#5D5D62">${ev.placeName || ''}</span>`,
      { direction: 'top', offset: [0, -8], className: 'place-tip' },
    );
    m.on('click', () => {
      emit('jumpToYear', ev.year);
    });
    m.addTo(allLayer);
  }
}

function updateActive(coords: [number, number]) {
  if (!map) return;
  if (activeMarker) {
    activeMarker.setLatLng(coords);
  } else {
    activeMarker = L.circleMarker(coords, {
      radius: 11,
      color: '#7C2F19',
      weight: 3,
      fillColor: '#C25A3A',
      fillOpacity: 1,
    }).addTo(map);
  }
  activeMarker.bringToFront?.();

  if (!expanded.value) {
    // Centre on the active point; don't drag Miseglia into the frame
    // (otherwise Japan / Cape Town trips show an ocean of nothing in the middle).
    map.flyTo(coords, 5, { duration: 0.6 });
  }
}

onBeforeUnmount(() => { map?.remove(); });
</script>

<style scoped>
.tmap {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 320px;
  height: 280px;
  background: var(--paper);
  border: 1px solid var(--vein-strong);
  border-radius: 6px;
  box-shadow: 0 20px 50px -20px rgba(27,27,31,0.5);
  /* Leaflet's own panes range up to z-index 700 in their stacking context,
     so the floating widget needs to clear them comfortably to sit on top. */
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s var(--ease-out),
    transform 0.4s var(--ease-out),
    width 0.42s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.42s cubic-bezier(0.4, 0, 0.2, 1),
    bottom 0.42s cubic-bezier(0.4, 0, 0.2, 1),
    right 0.42s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}
.tmap--visible { opacity: 1; transform: translateY(0); pointer-events: auto; }
.tmap--collapsed { height: 56px; }
.tmap--collapsed .tmap__canvas { display: none; }
.tmap--expanded {
  width: min(1100px, 92vw);
  height: 86vh;
  bottom: 4vh;
  right: 4vw;
  left: auto;
  box-shadow: 0 30px 80px -16px rgba(27,27,31,0.55);
}

.tmap__head {
  display: flex;
  align-items: stretch;
  background: var(--slate);
  color: var(--paper);
  height: 56px;
  flex-shrink: 0;
}
.tmap__toggle-area {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0 1rem;
  background: transparent;
  border: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.tmap__toggle-area:hover { background: rgba(255,255,255,0.05); }
.tmap__head-txt {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
  flex: 1 1 auto;
  /* Lock to a single line — prevents the header height from shifting
     between entries, which made the map canvas reproject its markers. */
  white-space: nowrap;
  overflow: hidden;
}
.tmap__year {
  font-family: var(--font-mono);
  font-weight: 400;
  font-size: 0.82rem;
  letter-spacing: 0;
  color: var(--paper);
  flex-shrink: 0;
  line-height: 1.14;
}
.tmap__sep {
  font-family: var(--font-mono);
  color: var(--granite-soft);
  flex-shrink: 0;
  opacity: 0.7;
}
.tmap__loc {
  font-family: var(--font-mono);
  font-size: 0.82rem;
  color: var(--paper);
  opacity: 0.7;
  letter-spacing: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.14;
}
.tmap__expand,
.tmap__collapse {
  background: transparent;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--granite-soft);
  cursor: pointer;
  width: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-family: monospace;
  flex-shrink: 0;
  padding: 0;
  transition: color 0.15s ease, background 0.15s ease;
}
.tmap__expand:hover,
.tmap__collapse:hover { color: var(--paper); background: rgba(255,255,255,0.06); }

.tmap__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  padding: 0.6rem 0.8rem;
  background: var(--paper-deep);
  border-bottom: 1px solid var(--vein);
}
.tmap__filter {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.7rem;
  background: rgba(255,255,255,0.55);
  border: 1px solid var(--vein);
  border-radius: 999px;
  font-size: 0.78rem;
  color: var(--slate-soft);
  cursor: pointer;
  transition: all 0.16s var(--ease-out);
  font-family: var(--font-mono);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.tmap__filter:hover { background: var(--paper); color: var(--slate); }
.tmap__filter.is-on {
  background: var(--accent);
  color: var(--paper);
  border-color: var(--accent-deep);
}
.tmap__filter-n {
  font-size: 0.72rem;
  background: rgba(27,27,31,0.07);
  padding: 0 0.45rem;
  border-radius: 999px;
  color: var(--slate);
}
.tmap__filter.is-on .tmap__filter-n {
  background: rgba(244,241,234,0.25);
  color: var(--paper);
}

.tmap__canvas {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  background: #ede9dc;
}

.tmap__foot {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0.7rem 1rem;
  background: var(--paper-deep);
  border-top: 1px solid var(--vein);
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--slate-soft);
  letter-spacing: 0;
}
.tmap__stat .num {
  font-family: var(--font-mono);
  font-weight: 700;
  color: var(--accent);
  font-size: 0.86rem;
  margin: 0 0.1rem;
}
.tmap__close-x {
  margin-left: auto;
  border: 1px solid var(--vein-strong);
  background: var(--paper);
  border-radius: 3px;
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  font-family: var(--font-mono);
}
.tmap__close-x:hover { background: var(--accent); color: var(--paper); border-color: var(--accent-deep); }

@media (max-width: 760px) {
  .tmap { width: 240px; bottom: 12px; right: 12px; }
  .tmap__canvas { height: 160px; }
  .tmap--expanded { width: 96vw; right: 2vw; bottom: 2vh; height: 90vh; }
}
</style>
